import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { useReportOdometerStore } from "../../../store/reports/reportOdometerStore";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard, ReportChart, tickColor, gridColor } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import type { ChartConfiguration } from "chart.js";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";

const MAX_RANGE_DAYS = 31;

interface OdometerFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
}

interface OdometerRow {
  id: string;
  unidad: string;
  kmInicial: string;
  kmFinal: string;
  kmRecorrido: string;
  primerRegistro: string;
  ultimoRegistro: string;
}

const ODOMETER_COLUMNS: ReportTableColumn<OdometerRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "kmInicial", header: "Km inicial", align: "right" },
  { key: "kmFinal", header: "Km final", align: "right" },
  { key: "kmRecorrido", header: "Km recorrido", align: "right" },
  { key: "primerRegistro", header: "Primer registro" },
  { key: "ultimoRegistro", header: "Último registro" },
];

const fmtKm = (n: number | null): string => (n == null ? "N/A" : `${n.toFixed(2)} km`);

export default function OdometerSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const loadRange = useReportOdometerStore((state) => state.loadRange);
  const clearOdometer = useReportOdometerStore((state) => state.clear);
  const resultsState = useReportOdometerStore((state) => state.results);
  const order = useReportOdometerStore((state) => state.order);
  const odometerLoading = useReportOdometerStore((state) => state.loading);
  const tz = useReportOdometerStore((state) => state.tz);

  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [loadAttempted, setLoadAttempted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<OdometerFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "odometer",
      typeDocument: "pdf",
    },
  });

  const { showAlert } = useAlert();
  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => {
    clearOdometer();
    setLoadAttempted(false);
  });
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const hasDevices = (selectedDevices?.length ?? 0) > 0;
  const hasDates = Boolean(startDate && endDate);
  const rangeDays = useMemo(() => {
    if (!hasDates) return 0;
    return (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
  }, [endDate, hasDates, startDate]);
  const hasValidRange = hasDates && rangeDays >= 0;
  const isWithinViewLimit = hasValidRange && rangeDays <= MAX_RANGE_DAYS;
  const filtersReadyForView = hasDevices && hasValidRange && isWithinViewLimit;

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: OdometerFormData) => {
    const result = await generateHistoryReport(
      {
        deviceIds: formData.devices,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeReport: formData.typeReport,
        typeDocument: formData.typeDocument,
      },
      showAlert,
      () => {},
    );

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type:
          formData.typeDocument === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const resolveNameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onViewOdometer = async () => {
    const { devices, startDate, endDate } = getValues();

    if (!devices || devices.length === 0) {
      showAlert({
        title: "Selecciona unidades",
        text: "Selecciona al menos un equipo para ver el odómetro.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    if (!startDate || !endDate) {
      showAlert({
        title: "Fechas requeridas",
        text: "Selecciona la fecha de inicio y fin.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / (24 * 60 * 60 * 1000);
    if (days > MAX_RANGE_DAYS) {
      showAlert({
        title: "Rango demasiado amplio",
        text: `Ver odómetro solo muestra hasta ${MAX_RANGE_DAYS} días. Para rangos mayores usa "Generar Reporte".`,
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
      return;
    }

    const fromIso = new Date(startDate).toISOString();
    const toIso = new Date(endDate).toISOString();
    const nameById = resolveNameById();
    const devicesToLoad = devices.map((id) => ({
      deviceId: id,
      vehicleName: nameById[id] || id,
    }));

    await loadRange(devicesToLoad, fromIso, toIso);
    setLoadAttempted(true);
  };

  const orderedResults = useMemo(
    () => order.map((id) => resultsState[id]).filter(Boolean),
    [order, resultsState],
  );

  const totals = useMemo(() => {
    let totalKm = 0;
    let vehiclesProcessed = 0;
    let maxKm = 0;
    orderedResults.forEach((r) => {
      if (r.kmRecorrido != null) {
        totalKm += r.kmRecorrido;
        vehiclesProcessed++;
        if (r.kmRecorrido > maxKm) maxKm = r.kmRecorrido;
      }
    });
    const avgKm = vehiclesProcessed > 0 ? totalKm / vehiclesProcessed : 0;
    return { totalKm, vehiclesProcessed, maxKm, avgKm };
  }, [orderedResults]);

  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), false), [tz]);

  const fmtTs = useCallback(
    (ts: number | null): string => (ts == null ? "N/A" : fmtDateTime(ts)),
    [fmtDateTime],
  );

  const tableRows = useMemo<OdometerRow[]>(
    () =>
      orderedResults.map((r) => ({
        id: r.deviceId,
        unidad: r.vehicleName,
        kmInicial: fmtKm(r.kmInicial),
        kmFinal: fmtKm(r.kmFinal),
        kmRecorrido: fmtKm(r.kmRecorrido),
        primerRegistro: fmtTs(r.firstTs),
        ultimoRegistro: fmtTs(r.lastTs),
      })),
    [orderedResults, fmtTs],
  );

  const chartConfig = useMemo<ChartConfiguration | null>(() => {
    const withKm = orderedResults.filter((r) => r.kmRecorrido != null);
    if (withKm.length === 0) return null;

    return {
      type: "bar",
      data: {
        labels: withKm.map((r) => r.vehicleName),
        datasets: [
          {
            label: "Km recorrido",
            data: withKm.map((r) => Number((r.kmRecorrido as number).toFixed(2))),
            backgroundColor: withKm.map((r) => r.color),
            borderRadius: 4,
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { displayColors: false } },
        scales: {
          x: {
            title: { display: true, text: "km", color: tickColor(isDarkMode) },
            beginAtZero: true,
            ticks: { color: tickColor(isDarkMode) },
            grid: { color: gridColor(isDarkMode) },
          },
          y: {
            ticks: { color: tickColor(isDarkMode), autoSkip: false, font: { size: 11 } },
            grid: { display: false },
          },
        },
      },
    };
  }, [orderedResults, isDarkMode]);

  const odoChartHeight = Math.max(220, orderedResults.filter((r) => r.kmRecorrido != null).length * 40);

  const onDownload = async (typeDocument: "pdf" | "excel") => {
    if (orderedResults.length === 0) return;
    const { startDate, endDate } = getValues();
    const extension = typeDocument === "pdf" ? "pdf" : "xlsx";
    const fileName = `odometer_${startDate.split("T")[0]}_${endDate.split("T")[0]}.${extension}`;
    const period =
      `Período: ${startDate.replace("T", " ")} al ${endDate.replace("T", " ")}` +
      ` | Km total: ${fmtKm(totals.totalKm)}`;

    const rows: OdometerRow[] = [
      ...tableRows,
      {
        id: "__total__",
        unidad: "TOTAL",
        kmInicial: "",
        kmFinal: "",
        kmRecorrido: fmtKm(totals.totalKm),
        primerRegistro: "",
        ultimoRegistro: "",
      },
    ];

    setExporting(typeDocument);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: "Reporte de Odómetro", period, fileName };
      if (typeDocument === "excel") {
        await exportHistoricToExcel(rows, ODOMETER_COLUMNS, meta);
      } else {
        exportHistoricToPdf(rows, ODOMETER_COLUMNS, meta);
      }
    } catch {
      showAlert({
        title: "Error al descargar",
        text: "No se pudo generar el archivo de odómetro.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        showCancelButton: false,
      });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    setValue("startDate", toLocalInputValue(new Date()));
    setValue("endDate", toLocalInputValue(new Date(new Date().setDate(new Date().getDate() + 15))));
  }, []);

  useEffect(() => {
    return () => {
      clearOdometer();
      setLoadAttempted(false);
    };
  }, [clearOdometer]);

  const hasResults = orderedResults.length > 0;
  const showSkeleton = odometerLoading;
  const showEmptyState = !hasResults && !showSkeleton;

  const summaryCards = [
    { label: "Km total flota", value: fmtKm(totals.totalKm), icon: "ri-route-line" },
    { label: "Promedio por unidad", value: fmtKm(totals.avgKm), icon: "ri-dashboard-3-line" },
    { label: "Mayor recorrido", value: fmtKm(totals.maxKm), icon: "ri-arrow-up-line" },
    { label: "Unidades con datos", value: String(totals.vehiclesProcessed), icon: "ri-truck-line" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Formato</label>
            <Controller
              name="typeDocument"
              control={control}
              render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton
            type="button"
            variant="secondary"
            onClick={onViewOdometer}
            disabled={odometerLoading || !filtersReadyForView || disableWithoutCompany}
            title={!filtersReadyForView && hasDevices && hasValidRange && !isWithinViewLimit ? `Ver odómetro solo admite hasta ${MAX_RANGE_DAYS} días` : undefined}
          >
            Ver odómetro
          </ReportActionButton>
          <ReportActionButton
            type="submit"
            disabled={loading || !hasDevices || !hasValidRange || disableWithoutCompany}
          >
            {loading ? "Generando..." : "Generar Reporte"}
          </ReportActionButton>
        </div>
      </form>

      {showSkeleton && (
        <ReportLoadingPanel
          isDarkMode={isDarkMode}
          icon="ri-dashboard-3-line"
          title="Calculando odómetro…"
          subtitle="Obteniendo lecturas del rango seleccionado"
        />
      )}

      {hasResults && !showSkeleton && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {totals.vehiclesProcessed} de {orderedResults.length} unidad(es) con datos
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => onDownload("pdf")}
                disabled={exporting !== null}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "pdf" ? "Generando..." : "Descargar PDF"}
              </button>
              <button
                type="button"
                onClick={() => onDownload("excel")}
                disabled={exporting !== null}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
              >
                {exporting === "excel" ? "Generando..." : "Descargar Excel"}
              </button>
              <button
                type="button"
                onClick={() => {
                  clearOdometer();
                  setLoadAttempted(false);
                }}
                className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
              >
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <ReportStatCard key={card.label} icon={card.icon} label={card.label} value={card.value} isDarkMode={isDarkMode} />
            ))}
          </div>

          {chartConfig && (
            <div className={`sticky top-0 z-20 p-3 rounded-xl border shadow-sm ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
              <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Km recorrido por unidad</p>
              <ReportChart config={chartConfig} height={odoChartHeight} />
            </div>
          )}

          <ReportPointsTable
            data={tableRows}
            columns={ODOMETER_COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="No se encontró odómetro en el rango seleccionado."
          />
        </div>
      )}

      {showEmptyState && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`relative w-full overflow-hidden rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`} style={{ minHeight: 220 }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
                <i className="ri-dashboard-3-line text-primary-500 text-2xl" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${themeClasses.text}`}>Odómetro por unidad</p>
                <p className={`text-xs mt-1 max-w-md ${themeClasses.textSecondary}`}>
                  Configura equipos y fechas, y usa <strong>Ver odómetro</strong> (hasta {MAX_RANGE_DAYS} días) para ver el km recorrido por unidad. Para rangos mayores usa <strong>Generar Reporte</strong>.
                </p>
              </div>
            </div>
          </div>

          {loadAttempted && !odometerLoading && (
            <div className={`p-3 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-100 border-gray-200"}`}>
              <div className="flex items-start space-x-2">
                <i className={`ri-information-line text-base ${themeClasses.textSecondary}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${themeClasses.textSecondary}`}>Sin resultados</p>
                  <p className={`text-sm ${themeClasses.text} mt-0.5`}>
                    No se encontró odómetro en el rango seleccionado. Verifica equipos y fechas, o usa <strong>Generar Reporte</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
